import {Component, NgZone} from "@angular/core";

import {ModalConfig} from "../services/notification/modal.config";
import {EventBus} from "../event/EventBus";
import "rxjs/add/operator/map";
import "rxjs/add/operator/subscribeOn";

import {ComponentBase} from "../base/ComponentBase";
import {BugsnagLoggingService} from "../services/logging/bugsnag-logging.service";
import {PipelineEvent, PIPELINE_STATUS} from "../event/PipelineEvent";
import {Observable} from "rxjs";

import {PipelineService} from "../services/pipeline/pipeline.service";
import {ProcessingTask} from "../services/pipeline/batch.pipeline.service";

import {IMetaData} from  "../api/data-model";
import {HttpClient} from "@angular/common/http";
import {filter} from "rxjs/operators";
import {ClientConfiguration} from "../services/configuration/client.config";
import { UtilitiesService } from "../services/utilities.service";
import { AudioQueueService } from "../services/audio-playback/audioqueue.service";

interface IExportSelection {
    [index: string]: boolean;
}

@Component({
    selector: "pipeline-batch-modal",
    templateUrl: "./pipeline.batch.modal.component.html",
    styleUrls: ["./pipeline.component.scss"]
})
export class PipelineScriptModalComponent extends ComponentBase {

    public activateModal: boolean = false;
    public status:string = "";
    public filteredTitles:Array<IMetaData> = new Array<IMetaData>();
    public exportSelections:IExportSelection = {};
    public filterNew:boolean = false;
    public filterUnpublished:boolean = false;
    public selectAll:boolean = false;
    public doScriptExport:boolean = true;
    public initialized:boolean = false;
    public isProgressModalVisible:boolean = false;
    public progressModalMessage:string = "";
    
    constructor (
        private http:HttpClient,
        private eventBus: EventBus,
        private modalConfig: ModalConfig,
        private audioQueueService:AudioQueueService,
        private logService: BugsnagLoggingService,
        private _ngZone: NgZone,
        private pipelineService: PipelineService
    ) {
        super("PipelineScriptModalComponent");
        console.log("constructing PipelineScriptModal");
        this.subscribeObserve();
    }

    private subscribeObserve():void {
        let sub = this.eventBus.pipe(filter((e:PipelineEvent) =>
                                       (e.type === PipelineEvent.TYPE) &&
                                       ((e.status === PIPELINE_STATUS.script) ||
                                        (e.status === PIPELINE_STATUS.batch))))
            .subscribe((e:PipelineEvent) => {
                console.log("activating pipeline batch modal, initialized=" + this.initialized);
                if (!this.initialized) {
                    this.pipelineService.populateData(this.filterNew, this.filterUnpublished).subscribe(filtered => {
                        this.filteredTitles = filtered;
                        this.initExportSelections();
                        this.initialized = true;
                    });
                } else {
                    this.filteredTitles = this.pipelineService.filterTitles("", this.filterNew, this.filterUnpublished);
                    this.initExportSelections();
                }
                this.activateModal = true;
                this.doScriptExport = (e.status === PIPELINE_STATUS.script);
            });
        this.subscriptions.push(sub);
    }

    public get isDevMode(): boolean {
        return this.pipelineService.isDevMode();
    }

    public getSelectedList(): string[] {
        return Object.keys(this.exportSelections).filter((s) => {
            return (this.exportSelections[s] === true);
        });
    }

    public fetchMetadata(path:string):Observable<Response> {
        return this.http.get(path) as Observable<Response>;
    }

    private initExportSelections():void {
        this.exportSelections = {};
        for (let title of this.filteredTitles) {
            this.exportSelections[title.referenceStory] = false;
        }
    }

    public exportScripts(isTestRun:boolean): void {
        let exportList: string[] = this.getSelectedList();
        this.pipelineService.exportScripts(exportList, isTestRun);
    }

    public batchProcess(isTestRun: boolean): void {
        let exportList: string[] = this.getSelectedList();
        this.pipelineService.batchProcess(exportList, isTestRun, ProcessingTask.DEFAULT);
    }

    public batchRenumberQuestions(isTestRun: boolean): void {
        let exportList: string[] = this.getSelectedList();
        this.pipelineService.batchRenumberQuestions(Object.keys(this.exportSelections), exportList, isTestRun);
    }

    public batchRenumberText(isTestRun: boolean): void {
        let exportList: string[] = this.getSelectedList();
        this.pipelineService.batchProcess(exportList, isTestRun, ProcessingTask.TEXT_RENUMBER);
    }

    public batchApplyExpansions(isTestRun: boolean): void {
        let exportList: string[] = this.getSelectedList();
        this.pipelineService.batchProcess(exportList, isTestRun, ProcessingTask.APPLY_EXPANSIONS);
    }

    public batchVerify(): void {
        let exportList: string[] = this.getSelectedList();
        this.pipelineService.batchVerify(exportList);
    }

    public batchInflate(): void {
        let exportList: string[] = this.getSelectedList();
        this.pipelineService.batchInflate(exportList);
    }

    public batchUpdateRecognizerData(): void {
        let exportList: string[] = this.getSelectedList();
        this.pipelineService.batchUpdateRecognizerData(exportList);
    }

    public importGlossary($event): void {
        let reader = new FileReader;
        reader.onload = (event) =>  this.pipelineService.importGlossary(event.target.result);
        reader.readAsText($event.target.files[0]);
    }

    public importQuestions($event): void {
        let reader = new FileReader;
        reader.onload = (event) => this.pipelineService.importQuestions(event.target.result);
        reader.readAsText($event.target.files[0]);
    }

    public convertSelections($event): void {
        let reader = new FileReader();
        reader.onload = (event) => this.pipelineService.convertSelections(event.target.result);
        reader.readAsText($event.target.files[0]);
    }

    /*public convertSelection(): void {
        this.pipelineService.convertSelections();
    }*/

    public closeModal(): void {
        this.filteredTitles.splice(0, this.filteredTitles.length);
        this.activateModal = false;
    }

    public setExportScript(referenceStory:string):void {
        this.exportSelections[referenceStory] = !this.exportSelections[referenceStory];
        console.log("set flag for " + referenceStory + " to " + this.exportSelections[referenceStory]);
    }

    public runFilterNew():void {
        this.filterNew = !this.filterNew;
        this.filterUnpublished = false;
        this.filteredTitles = this.pipelineService.filterTitles("", this.filterNew, this.filterUnpublished);
        this.initExportSelections();
    }

    public runFilterUnpublished():void {
        this.filterUnpublished = !this.filterUnpublished;
        this.filterNew = false;
        this.filteredTitles = this.pipelineService.filterTitles("", this.filterNew, this.filterUnpublished);
        this.initExportSelections();
    }

    public runSelectAll():void {
        this.selectAll = !this.selectAll;
        this._ngZone.run(() => {
            Object.keys(this.exportSelections).forEach((title) => {
                this.exportSelections[title] = this.selectAll;
            });
        });
    }

    public async batchVerifyImages(isTestRun: boolean): Promise<void> {
        let selectionList: string[] = this.getSelectedList();
        let batchVerifyImageErrors = {};

        if(selectionList.length>0){
            this.isProgressModalVisible = true;
            this.progressModalMessage = "Loading...."
        }

        for (let i = 0; i < selectionList.length; i++) {
            // set currentSourceSelection
            this.progressModalMessage = `Receiving.... ${selectionList[i]}`;
            await this.pipelineService.retrieveUninflatedContent(selectionList[i]).toPromise();
            await this.pipelineService.inflateContent().toPromise();

            // verify images
            this.progressModalMessage = `Verifying.... source for ${selectionList[i]}`;
            batchVerifyImageErrors[selectionList[i]] = this.pipelineService.analyzeSelectionImages();
        }
        this.isProgressModalVisible = false;
        UtilitiesService.downloadFile(JSON.stringify(batchVerifyImageErrors), "image-errors.json");
    }
    
    public async batchVerifyAudios(isTestRun: boolean): Promise<void> {
        let selectionList: string[] = this.getSelectedList();
        let batchVerifyAudioErrors = {};

        if(selectionList.length>0){
            this.isProgressModalVisible = true;
            this.progressModalMessage = "Loading...."
        }

        this.pipelineService.resetMissingAudioDataOfSelections();
        for (let i = 0; i < selectionList.length; i++) {
            batchVerifyAudioErrors[selectionList[i]] = {};

            // set currentSourceSelection
            this.progressModalMessage = `Receiving.... ${selectionList[i]}`;
            await this.pipelineService.retrieveUninflatedContent(selectionList[i]).toPromise();
            await this.pipelineService.inflateContent().toPromise();


            // verify into and title audio
            this.audioQueueService.hardReset();
            this.progressModalMessage = `Verifying.... intro and title Audios for ${selectionList[i]}`;
            const intoAndTitleErrors = await this.pipelineService.analyzeIntoAndTitleAudio().take(1).toPromise();
            batchVerifyAudioErrors[selectionList[i]]['introAndTitle'] = intoAndTitleErrors;

            // verify readToMeAudios
            this.audioQueueService.hardReset();
            this.progressModalMessage = `Verifying.... Read to me Audios for ${selectionList[i]}`;
            const readToMeAudioErrors = await this.pipelineService.analyzeReadToMeAudio().take(1).toPromise();
            batchVerifyAudioErrors[selectionList[i]]['readToMeAudio'] = readToMeAudioErrors;

            // verify wordAudios
            this.audioQueueService.hardReset();
            this.progressModalMessage = `Verifying.... Word Audios for ${selectionList[i]}`;
            const wordAudioErrors = await this.pipelineService.analyzeWordAudio().take(1).toPromise();
            batchVerifyAudioErrors[selectionList[i]]['wordAudio'] = wordAudioErrors;


            // verify glossaryAudios
            this.audioQueueService.hardReset();
            this.progressModalMessage = `Verifying.... Glossary Audios for ${selectionList[i]}`;
            const glossaryAudioErrors = await this.pipelineService.analyzeGlossaryAudio().take(1).toPromise();
            batchVerifyAudioErrors[selectionList[i]]['glossaryAudio'] = glossaryAudioErrors;


            // verify questionAudios
            this.audioQueueService.hardReset();
            this.progressModalMessage = `Verifying.... Question Audios for ${selectionList[i]}`;
            const questionAudioErrors = await this.pipelineService.analyzeQuestionAudio().take(1).toPromise();
            batchVerifyAudioErrors[selectionList[i]]['questionAudio'] = questionAudioErrors;

        }
        UtilitiesService.downloadFile(JSON.stringify(batchVerifyAudioErrors), "audio-errors.json");
        UtilitiesService.downloadFile(JSON.stringify(this.pipelineService.getMissingAudioData()), "audio-corrupt-data.json");
        this.isProgressModalVisible = false;
    }

    public async batchVerifyMetaData(isTestRun: boolean): Promise<void> {
        let selectionList: string[] = this.getSelectedList();
        let batchVerifyMetaDataErrors = {};

        if(selectionList.length>0){
            this.isProgressModalVisible = true;
            this.progressModalMessage = "Loading...."
        }
        
        for (let i = 0; i < selectionList.length; i++) {
            // set currentSourceSelection
            this.progressModalMessage = `Receiving.... ${selectionList[i]}`;
            await this.pipelineService.retrieveUninflatedContent(selectionList[i]).toPromise();
            
            // verify metadata
            this.progressModalMessage = `Verifying.... MetaData for ${selectionList[i]}`;
            const isValid = this.pipelineService.analyzeSelectionMetaData();
            const referenceStory = this.pipelineService.currentSourceSelection.metaData.referenceStory;
            const metadata = this.pipelineService.currentSourceSelection.metaData;
            if(!isValid){
                batchVerifyMetaDataErrors[referenceStory]=metadata;
            }
        }
        this.isProgressModalVisible = false;
        UtilitiesService.downloadFile(JSON.stringify(batchVerifyMetaDataErrors), "metaData-to-review.json");
    }
}
